// The layout registry — the inversion that replaces v3's `ALL_CONFIGS` static map (ledger B1 /
// §A13, derived independently by 24 §18 Q1, 25 §18 Q2, 27 §18 Q1). v3's `configs/AllConfigs.js`
// imported all 22 layouts, and four of them import a VERTICAL's own section component
// (`ProductPerformance.js:1` → `BasketAnalysisChart.vue`, and the Benchmark / AppAcquisition /
// RFMClustering pairs). Porting that map as-is forces `features/reports` to deep-import
// `features/{audience,commerce-analytics,marketing-insights}` — a boundary error and an import
// cycle, since every vertical hard-depends on 08. So layouts are pushed IN, never pulled.
//
// Same idiom as 05's `navContributions` and 22's `settingsNavRegistry`: module state (not a Pinia
// store), a register call that returns its own undo, an explicit reset for suite isolation.
// Deliberately NOT sealed — see `registerReportLayout`.
import { shallowReactive } from 'vue'

import type { ReportLayoutConfig } from '../models/layoutConfig.types'

import { SEED_REPORT_LAYOUTS, type ReportLayoutKey } from './layouts'

import { useEnv } from '@/shared/config'

// `shallowReactive` for the same reason 22 uses it on `navEntries`: `ReportPage`'s layout is read
// inside a `computed`, so a registration that lands after that first read still repairs the page
// instead of leaving it blank. Shallow is load-bearing too — a section may carry a component
// reference (`ReportSectionConfig.component`), and deep reactivity would proxy it.
const layouts = shallowReactive(new Map<string, ReportLayoutConfig>())

/** Guarded like 22's: before bootstrap's `validateEnv()` there is no env, and "cannot tell" must
 *  behave as non-production so a composition bug fails loud in dev/CI rather than in a browser. */
function isProduction(): boolean {
  try {
    return useEnv().MODE === 'production'
  } catch {
    return false
  }
}

/** 08's own 15 layouts are registrations like any other — they just run first, at module eval. */
function seedOwnLayouts(): void {
  for (const [key, config] of Object.entries(SEED_REPORT_LAYOUTS)) layouts.set(key, config)
}
seedOwnLayouts()

/**
 * Register one report layout under `key` (ledger §A11 registry-inversion set). The config may
 * embed the registrant's own components via `ReportSectionConfig.component`; 08 renders them
 * without ever importing the owning feature.
 *
 * Idempotent by identity: re-registering the SAME config object for the same key is a no-op
 * (module re-evaluation / HMR). A DIFFERENT config for a key that is already taken is a
 * composition bug — it throws outside production so CI catches the collision, and in production
 * warns + last-registration-wins rather than white-screening a page (22 §14 precedent).
 *
 * There is **no seal**, unlike 22's `registerSettingsChildRoutes`. That registry must seal because
 * `createRouter()` snapshots the child-route array, so a late record can never resolve. Layouts
 * are not snapshotted: a page route is composed from the page registry, while its layout is read
 * at RENDER time through a reactive getter. Sealing would only break the case verticals need — a layout
 * registered from its own lazily-imported route chunk, i.e. long after `createAppRouter()`.
 *
 * @returns the unregister for this exact registration (idempotent; a no-op once superseded).
 */
export function registerReportLayout(key: ReportLayoutKey, config: ReportLayoutConfig): () => void {
  const existing = layouts.get(key)
  if (existing !== undefined && existing !== config) {
    const message =
      `[reports] duplicate report layout key "${key}" — layout keys must be unique across ` +
      `every registrant (08's own keys are ${Object.keys(SEED_REPORT_LAYOUTS).length} seeds)`
    if (!isProduction()) throw new Error(message)
    console.warn(`${message}; last registration wins`)
  }
  layouts.set(key, config)
  return () => {
    if (layouts.get(key) === config) layouts.delete(key)
  }
}

/** The registered layout for `key`, or `undefined` when nothing has claimed it yet. */
export function reportLayout(key: ReportLayoutKey): ReportLayoutConfig | undefined {
  return layouts.get(key)
}

/** Every claimed key — the manifest cross-check and a vertical's own registration assertions. */
export function registeredReportLayoutKeys(): ReportLayoutKey[] {
  return [...layouts.keys()]
}

/** Test isolation: drop every registrant's layout and restore 08's own seeds. */
export function resetReportLayoutRegistry(): void {
  layouts.clear()
  seedOwnLayouts()
}
